import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../config';
import { startOfDay, endOfDay } from 'date-fns';
import { entrySource, MANUAL, type EntrySource, type HealthPlatform } from '../../health/healthImportModel';
import { recordImportTombstone } from './healthImport';

export interface Activity {
  id: string;
  userId: string;
  name: string;
  startTime: Date;
  endTime: Date;
  notes?: string;
  calories?: number;
  createdAt: Date;
  updatedAt: Date;
  /**
   * Where this entry came from. A stored value that is absent — or anything
   * other than 'health' — classifies as manual, exactly as it does on mobile;
   * see entrySource.
   */
  source?: EntrySource;
  /** Imported entries only. */
  healthPlatform?: HealthPlatform;
  /** Imported entries only: the raw platform record id. */
  healthRecordId?: string;
}

export interface CreateActivityParams {
  userId: string;
  name: string;
  startTime: Date;
  endTime: Date;
  notes?: string;
  calories?: number;
}

export interface UpdateActivityParams {
  name?: string;
  startTime?: Date;
  endTime?: Date;
  notes?: string;
  calories?: number;
}

// Get today's activities for a user
export async function getTodayActivities(userId: string): Promise<Activity[]> {
  try {
    const today = new Date();
    const start = startOfDay(today);
    const end = endOfDay(today);
    
    const activitiesQuery = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      where('startTime', '>=', Timestamp.fromDate(start)),
      where('startTime', '<=', Timestamp.fromDate(end)),
      orderBy('startTime', 'desc')
    );
    
    const snapshot = await getDocs(activitiesQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime?.toDate() || new Date(),
        endTime: data.endTime?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // AFTER the spread: the raw stored value must never reach the UI.
        source: entrySource(data.source),
      } as Activity;
    });
  } catch (error) {
    console.error('Error fetching activities:', error);
    throw error;
  }
}

// Get activities for a specific date range
export async function getActivitiesByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Activity[]> {
  try {
    const start = startOfDay(startDate);
    const end = endOfDay(endDate);
    
    const activitiesQuery = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      where('startTime', '>=', Timestamp.fromDate(start)),
      where('startTime', '<=', Timestamp.fromDate(end)),
      orderBy('startTime', 'desc')
    );
    
    const snapshot = await getDocs(activitiesQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime?.toDate() || new Date(),
        endTime: data.endTime?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // AFTER the spread: the raw stored value must never reach the UI.
        source: entrySource(data.source),
      } as Activity;
    });
  } catch (error) {
    console.error('Error fetching activities by date range:', error);
    throw error;
  }
}

// Create an activity
export async function createActivity(params: CreateActivityParams): Promise<string> {
  try {
    const activityData = {
      userId: params.userId,
      name: params.name,
      startTime: Timestamp.fromDate(params.startTime),
      endTime: Timestamp.fromDate(params.endTime),
      notes: params.notes || '',
      calories: params.calories || null,
      source: MANUAL,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'activities'), activityData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating activity:', error);
    throw error;
  }
}

// Update an activity
export async function updateActivity(activityId: string, params: UpdateActivityParams): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: Timestamp.now()
    };
    
    if (params.name !== undefined) updateData.name = params.name;
    if (params.startTime !== undefined) updateData.startTime = Timestamp.fromDate(params.startTime);
    if (params.endTime !== undefined) updateData.endTime = Timestamp.fromDate(params.endTime);
    if (params.notes !== undefined) updateData.notes = params.notes;
    if (params.calories !== undefined) updateData.calories = params.calories;
    
    await updateDoc(doc(db, 'activities', activityId), updateData);
  } catch (error) {
    console.error('Error updating activity:', error);
    throw error;
  }
}

// Delete an activity
export async function deleteActivity(activityId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'activities', activityId));
    // Without this, the phone's next import pass recreates the entry the user
    // just deleted here.
    await recordImportTombstone(activityId);
  } catch (error) {
    console.error('Error deleting activity:', error);
    throw error;
  }
}

